import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ContentWrapper,
  CheckoutStep,
} from '@features/Checkout/components/CheckoutSteps/CheckoutSteps.module.css';
import {
  useCreateUserMutation,
  useGetMeQuery,
  useUpdateSubscriptionsMutation,
  useAddMembershipsMutation,
} from '@shared/services/users';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLoginAsExistingUser,
  setOrderId,
  setUserData,
} from '@features/Checkout/store';
import CheckoutSidebar from '@features/Checkout/components/CheckoutSidebar';
import LoginForm from '@features/Auth/components/LoginForm';
import ConditionalWrapper from '@components/ConditionalWrapper';
import LoadingIcon from '@components/LoadingIcon';
import { useTranslation } from 'next-i18next';
import useAuth from '@features/MyAccount/hooks/use-auth';
import {
  useGetSummariesMutation,
  useRemoveUserFromMembershipMutation,
} from '@features/Checkout/services/checkout';
import { captureException } from '@sentry/nextjs';
import { useErrorHandler } from 'react-error-boundary';
import { pushBeginCheckoutToDataLayer } from '@utilities/helpers/common';
import {
  Checkbox as CheckboxClass,
  ErrorMessage,
} from './Information.module.css';
import NewUserForm from './components/NewUserForm';
import ExistingUserForm from './components/ExistingUserForm';
import ConsentCheckbox from './components/ConsentCheckbox';
import NewsLetterCheckbox from './components/NewsletterCheckbox';
import { useCheckUserSubscriptionsQuery } from '@shared/services/common';
import { isMarketingSubscriptionActive } from '@utilities/helpers/account';

const Information = ({ onStepCompleted, previousStep }) => {
  const { t } = useTranslation(['account', 'common', 'checkout']);
  const {
    isBusiness,
    membershipIds,
    user: { email, emailConfirm, phone, marketingConsent } = {},
    business = {},
    loginAsExistingUser,
    isSingle,
    couponCode,
    terminalSession,
  } = useSelector((state) => state.checkout);
  const {
    isAuthenticated: isAuth,
    isLoading: isFetchingAuth,
    switchUserCheckout,
  } = useAuth(false);
  const { data: user, isLoading: isLoadingUser } = useGetMeQuery(null, {
    skip: !isAuth,
  });
  const handleError = useErrorHandler();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [createUser] = useCreateUserMutation();
  const [updateSubscriptions] = useUpdateSubscriptionsMutation();
  const [addMemberships, { isSuccess: addedUserToMembership }] =
    useAddMembershipsMutation();
  const [removeUserFromMembership, { isSuccess: removedUserFromMembership }] =
    useRemoveUserFromMembershipMutation();
  const [
    getSummaries,
    { data: membershipsSummary, isLoading: isFetchingSummary },
  ] = useGetSummariesMutation({
    fixedCacheKey: 'summary-shared',
  });

  const {
    data: userSubscriptionsData = [],
    isLoading: isLoadingSubscriptions,
  } = useCheckUserSubscriptionsQuery(user?.uid, {
    skip: !user?.uid,
  });

  const isMarketingActive = isMarketingSubscriptionActive(
    userSubscriptionsData
  );

  const {
    options: { currencyCode },
  } = useSelector((state) => state.wordpress);

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      email,
      emailConfirm,
      phone,
      marketingConsent: isMarketingActive || marketingConsent,
    },
  });

  // Data layers
  useEffect(() => {
    if (!membershipsSummary) return;

    pushBeginCheckoutToDataLayer(
      !isAuth,
      membershipsSummary.summary,
      membershipsSummary.total,
      currencyCode,
      couponCode
    );
  }, [membershipsSummary, couponCode, currencyCode, isAuth]);

  useEffect(() => {
    if (!membershipIds.length || isFetchingSummary) return;

    getSummaries({
      ids: membershipIds,
      coupons: [couponCode],
      vat: isBusiness,
      terminalSession,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedUserToMembership, removedUserFromMembership]);

  // if user is not in membership, then set current user to membership.
  useEffect(() => {
    if (!membershipsSummary || !user) return;

    const addUserToMemberships = async () => {
      const userOnMembership = await membershipsSummary.summary.some(
        (membership) => membership.user
      );
      if (!userOnMembership && user) {
        addMemberships(membershipIds);
      }
    };

    addUserToMemberships();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, membershipsSummary]);

  /**
   * This method will be called when the form has been submitted and has
   * passed validation. Feel free to add any extra validation you may want
   * in this callback. Simply end by calling onStepCompleted();
   */
  const onSubmit = useCallback(
    async ({ marketingConsent: consent, ...data }) => {
      if (isAuth && isMarketingActive) {
        // remove any previous orderId reference
        dispatch(setOrderId(null));

        // remove memberships from cart
        // dispatch(setMembershipIds([]));

        // go next step
        onStepCompleted();
      }

      if (consent) {
        window.dataLayer.push({
          event: 'user details',
          'user details': {
            email,
            phone,
          },
        });
      }

      const userData = {
        ...data,
        memberships: membershipIds,
        consent,

        ...(isBusiness && {
          isBusiness,
          businessName: business.name || '',
          vatNumber: business.vatNumber || '',
        }),
      };
      const requestHandler = isAuth ? updateSubscriptions : createUser;

      requestHandler(userData)
        .unwrap()
        .then(() => {
          // remove any previous orderId reference
          dispatch(setOrderId(null));

          // remove memberships from cart
          // dispatch(setMembershipIds([]));

          // go next step
          onStepCompleted();
        })
        .catch((err) => {
          const errorMessages = Object.entries(err?.data?.message || {}).map(
            ([/* name, */ { msg /* type */ }]) => msg
          );
          setError(
            errorMessages.length
              ? errorMessages
              : [err?.data?.response || 'Unknown server error']
          );
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuth,
      membershipIds,
      isBusiness,
      business.name,
      business.vatNumber,
      updateSubscriptions,
      createUser,
      dispatch,
      onStepCompleted,
    ]
  );

  const handleFormChange = () => {
    dispatch(setUserData(getValues()));
  };

  useEffect(() => {
    if (!membershipIds?.length) {
      previousStep();
    }
  }, [membershipIds, previousStep]);

  const SignupForm = isAuth ? ExistingUserForm : NewUserForm;

  const sidebarButtonText = () => {
    if (isAuth && user?.has_card) {
      return t('payNow', { ns: 'common' });
    }
    if (isSingle) {
      return t('addCreditCard', { ns: 'checkout' });
    }
    return t('goToPayment', { ns: 'common' });
  };

  if (isLoadingUser || isLoadingSubscriptions) {
    return <LoadingIcon withWrapper size="large" />;
  }

  return (
    <div>
      <ConditionalWrapper
        condition={!loginAsExistingUser}
        wrapper={(children) => (
          <form
            className={ContentWrapper}
            onSubmit={handleSubmit(onSubmit)}
            onChange={handleFormChange}
          >
            {children}
          </form>
        )}
      >
        <ConditionalWrapper
          condition={loginAsExistingUser}
          wrapper={(children) => (
            <div className={ContentWrapper}>{children}</div>
          )}
        >
          <CheckoutSidebar
            buttonText={sidebarButtonText()}
            // fixedButtonOnMobile
            noButton={loginAsExistingUser}
            proceedDisabled={isSubmitting}
            memberships={membershipsSummary?.summary}
            checkoutPrice={membershipsSummary?.total}
            isInformationStep
          >
            <ConsentCheckbox className={CheckboxClass} />
            {!isAuth && (
              <NewsLetterCheckbox className={CheckboxClass} control={control} />
            )}
            {error && (
              <div className={ErrorMessage}>
                {t('somethingWentWrong', { ns: 'common' })}: {error}
              </div>
            )}
          </CheckoutSidebar>
          <div className={CheckoutStep}>
            {loginAsExistingUser ? (
              <LoginForm
                control={control}
                handleSubmit={handleSubmit}
                onLoginSuccess={() => {
                  dispatch(setLoginAsExistingUser(false));
                  addMemberships(membershipIds)
                    .unwrap()
                    .catch((err) => setError(err && err));
                }}
                onClickBecomeMember={(e) => {
                  e.preventDefault();
                  dispatch(setLoginAsExistingUser(false));
                }}
                defaultValues={{
                  email,
                }}
                showBecomeMemberLink={false}
              />
            ) : (
              <>
                {isFetchingAuth ? (
                  <LoadingIcon withWrapper />
                ) : (
                  <SignupForm
                    {...{
                      errors,
                      control,
                      getValues,
                      setValue,
                      removeUserFromMembership: () =>
                        removeUserFromMembership({
                          memberships: membershipIds,
                        })
                          .then(() => {
                            switchUserCheckout();
                          })
                          .catch((err) => {
                            captureException('Error switching user', err);
                            handleError(err);
                          }),
                    }}
                  />
                )}
              </>
            )}
          </div>
        </ConditionalWrapper>
      </ConditionalWrapper>
    </div>
  );
};

Information.propTypes = {};

export default Information;
